import { Box, Button, FormControl, Grid, InputLabel, MenuItem, Select, SelectChangeEvent, Stack, SvgIcon, Tab, Tabs, TextField, useMediaQuery, useTheme } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { GetTransfersResponse } from '../../app/services/api/generated';
import { UserContext } from '../../components/shared/useUser';
import { useTranslation } from 'react-i18next';
import { Plus, SearchSm } from './../../assets';
import { StockTransfersFilterOptions } from './interfaces/StockTransfersFilterOption';
import { StockTransfersTab } from './enums/StockTransfersTab';
import useNswagClient from '../../hooks/api/useNswagClient';
import SubmitModal from './components/StockTransfersCreateModal';
import PageTitle from '../../components/shared/PageTitle';
import PageArea from '../../components/shared/PageArea';
import useLogError from '../../hooks/useLogError';
import { Section } from '../../components/ui/Section';
import StockTransfersTable from './components/StockTransfersTable';
import { StockTransferType } from './interfaces/StockTransferType';
import { handleSearchFieldKeyDown } from '../../utils';

export const stockTransferTypes: StockTransferType[] = [
  { value: 'All', description: 'type.all' },
  { value: 'Outgoing', description: 'type.outgoing' },
  { value: 'Incoming', description: 'type.incoming' },
];

const StockTransfersPage = () => {
  const theme = useTheme();

  const [stockTransfers, setStockTransfers] = useState<GetTransfersResponse>({});
  const [activeTab, setActiveTab] = useState<StockTransfersTab>(StockTransfersTab.ACTIVE);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const { t } = useTranslation('stockTransfers');
  const { selectedStore, hasPermissionTo } = useContext(UserContext);
  const { getTransfers } = useNswagClient();
  const { logError } = useLogError();

  const defaultSort = 'transferDate';
  const defaultDirection = 'desc';
  const defaultTake = 10;

  //Header sort
  const [order, setOrder] = useState<'asc' | 'desc'>(defaultDirection);
  const [orderBy, setOrderBy] = useState<string>(defaultSort);
  const [loading, setLoading] = useState(false);

  const [transferNumberSearch, setTransferNumberSearch] = useState<number | undefined>();
  const [lastTransferNumberSearch, setLastTransferNumberSearch] = useState<number | undefined>();
  const [stockTransferType, setStockTransferType] = useState('All');
  const [date, setDate] = useState('');
  const [page, setPage] = useState(0);

  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`);

  useEffect(() => {
    reloadData();
  }, [page, orderBy, order]);

  useEffect(() => {
    setPage(0);
    setOrder(defaultDirection);
    setOrderBy(defaultSort);
    reloadData();
  }, [activeTab]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: StockTransfersTab) => {
    setActiveTab(newValue);
  };

  const handleConfirmModal = async () => {
    setOpenModal(true);
  };

  const handleCancelModal = (isReloadData: boolean) => {
    setOpenModal(false);

    if (isReloadData) {
      reloadData();
    }
  };

  const loadFilterData = (options: StockTransfersFilterOptions) => {
    setLoading(true);
    const {
      transferNumber,
      transferType,
      requestedDate,
      sort,
      direction,
      skip,
      take,
    } = options;
    setLastTransferNumberSearch(transferNumber);

    const storeNumber = selectedStore?.storeNumber;

    getTransfers(storeNumber, transferNumber, transferType, requestedDate, sort, direction, skip, take, activeTab !== StockTransfersTab.ACTIVE)
      .then((result) => {
        if (result?.data) {
          setStockTransfers(result.data);
        }
      })
      .catch((error) => {
        logError(error);
      })
      .finally(() => { setLoading(false); });
  };

  const callLoadDataFromAPI = (options: StockTransfersFilterOptions) => {
    loadFilterData(options);
  };

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDate(event.target.value);
  };

  const handleTransferNumberSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value;

    // Remove all non-digit characters
    const cleanedValue = rawValue.replace(/\D/g, '');

    if (cleanedValue === '') {
      setTransferNumberSearch(undefined); // or '' if your state is string-based
    } else {
      setTransferNumberSearch(Number(cleanedValue));
    }
  };

  const handleStockTransferTypeChange = (event: SelectChangeEvent<string>) => {
    setStockTransferType(event.target.value);
  };

  const clearAll = () => {
    setTransferNumberSearch(undefined);
    setDate('');
    setStockTransferType('All');
    callLoadDataFromAPI({
      transferNumber: undefined,
      transferType: '',
      requestedDate: '',
      sort: orderBy,
      direction: order,
      skip: 0,
      take: 10,
    });

    setPage(0);
  };

  const searchClick = () => {
    const transfertype = stockTransferType === 'All' ? '' : stockTransferType;
    callLoadDataFromAPI({
      transferNumber: transferNumberSearch,
      transferType: transfertype,
      requestedDate: date,
      sort: orderBy,
      direction: order,
      skip: 0,
      take: defaultTake,
    });

    setPage(0);
  };

  const reloadData = () => {
    const transfertype = stockTransferType === 'All' ? '' : stockTransferType;
    console.log(transferNumberSearch);
    callLoadDataFromAPI({
      transferNumber: transferNumberSearch,
      transferType: transfertype,
      requestedDate: date,
      sort: orderBy,
      direction: order,
      skip: page * 10,
      take: defaultTake,
    });
  };

  return (
    <PageArea>
      <Grid container
        spacing={3}>
        <Grid item
          xs={12}>
          <Stack width='100%'
            direction={isMobile ? 'column' : 'row'}
            sx={{
              justifyContent: 'space-between',
            }}>
            <Box width={isMobile ? '100%' : 'auto'}>
              <PageTitle subTitle='Record and track your transfers'>{t('title')}</PageTitle>
            </Box>
            {(hasPermissionTo(['IncomingTransferWrite']) || hasPermissionTo(['OutgoingTransferWrite'])) && (
              <Box width={isMobile ? '100%' : 'auto'}
                sx={{
                  display: 'flex',
                  justifyContent: { xs: 'flex-start', md: 'flex-end' },
                }}>
                <Button
                  variant='primary'
                  size='lg'
                  onClick={() => handleConfirmModal()}
                  startIcon={<SvgIcon><Plus fill='white' /></SvgIcon>}>{t('create')}
                </Button>
              </Box>
            )}
          </Stack>
        </Grid>
        <Grid container
          item
          xs={12}
          spacing={5}>
          <Grid item
            xl={3}
            lg={3}
            sm={4}
            xs={12}>
            <FormControl fullWidth
              size='small'>
              <TextField id="transfer-number-filter"
                label={t('filter.search_placeholder')}
                size='small'
                variant="outlined"
                value={transferNumberSearch ?? ''}
                inputProps={{
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                }}
                onChange={handleTransferNumberSearchChange}
                onKeyDown={e => handleSearchFieldKeyDown(e, searchClick)} />
            </FormControl>
          </Grid>

          <Grid item
            xl={2}
            lg={2}
            sm={4}
            xs={12}>
            <FormControl fullWidth
              size='small'>
              <InputLabel id="type-label">{t('filter.type_placeholder')}</InputLabel>
              <Select
                labelId="label"
                id="transfer-type-filter"
                value={stockTransferType}
                label={t('filter.type_placeholder')}
                onChange={handleStockTransferTypeChange}
              >
                {stockTransferTypes.map((type) => (
                  <MenuItem key={type.value}
                    value={type.value}>{t(type.description)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item
            xl={2}
            lg={3}
            sm={4}
            xs={12}>
            <FormControl fullWidth
              size='small'>
              <TextField id="transfer-date-filter"
                label={t('filter.date_placeholder')}
                type='date'
                size='small'
                variant="outlined"
                value={date}
                InputLabelProps={{ shrink: true }}
                onChange={handleDateChange} />
            </FormControl>
          </Grid>

          <Grid item
            xl={2}
            lg={2}
            sm={3}
            xs={12}
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'left' }}>
            <Button
              variant="secondary"
              size="lg"
              onClick={clearAll}
              fullWidth
            >
              {t('filter.clear')}
            </Button>
          </Grid>

          <Grid item
            xl={2}
            lg={2}
            sm={3}
            xs={12}
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'left' }}>
            <Button
              variant="primary"
              size="lg"
              startIcon={<SvgIcon> <SearchSm /> </SvgIcon>}
              onClick={searchClick}
              fullWidth
            >
              {t('filter.search')}
            </Button>
          </Grid>
          <Grid
            item
            xs={12}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              textColor="primary"
              indicatorColor="primary"
              aria-label="stock-transfers-tabs">
              <Tab
                label={t('available')}
                value={StockTransfersTab.ACTIVE} />
              <Tab
                label={t('archived')}
                value={StockTransfersTab.ARCHIVED} />
            </Tabs>
          </Grid>
          <Grid item
            xs={12}>
            <Section>
              <StockTransfersTable list={stockTransfers}
                activeTab={activeTab}
                order={order}
                orderBy={orderBy}
                setOrder={setOrder}
                setOrderBy={setOrderBy}
                loading={loading}
                page={page}
                setPage={setPage}
                transferNumberSearch={lastTransferNumberSearch}
                clearAll={clearAll}
                handleConfirmModal={handleConfirmModal}
              ></StockTransfersTable>
            </Section>
          </Grid>
        </Grid>
        <SubmitModal openModal={openModal}
          onCancel={handleCancelModal}></SubmitModal>
      </Grid>
    </PageArea>
  );
};

export default StockTransfersPage;